import { useState, type FormEvent } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/auth-layout";
import { PASSWORD_MIN, PasswordField } from "@/components/auth/password-field";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { authClient } from "@/lib/auth-client";
import { authErrorMessage } from "@/lib/i18n-errors";

type ResetSearch = { token?: string; error?: string };

export const Route = createFileRoute("/reset-password")({
  // Better Auth redirects here with `?token=…` on a valid link, or
  // `?error=INVALID_TOKEN` when the link is bad or has expired.
  validateSearch: (search): ResetSearch => ({
    token: typeof search.token === "string" ? search.token : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const { t } = useTranslation("auth");
  const { t: tErrors } = useTranslation("errors");
  const { token, error: linkError } = Route.useSearch();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const again = (
    <Link
      to="/forgot-password"
      className="text-foreground underline decoration-border-strong underline-offset-4 hover:decoration-foreground"
    >
      {t("reset.again")}
    </Link>
  );

  if (!token || linkError) {
    return (
      <AuthLayout title={t("reset.invalid")} description={t("reset.invalidBody")} footer={again}>
        {null}
      </AuthLayout>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < PASSWORD_MIN) {
      setPasswordError(t("password.tooShort", { min: PASSWORD_MIN }));
      return;
    }
    setPasswordError(null);
    setPending(true);
    const result = await authClient.resetPassword({ newPassword: password, token });
    if (result.error) {
      setError(authErrorMessage(result.error, tErrors));
      setPending(false);
      return;
    }
    toast(t("reset.done"));
    void navigate({ to: "/login" });
  }

  return (
    <AuthLayout title={t("reset.title")}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <PasswordField
          id="password"
          label={t("reset.newPassword")}
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
          hint={t("password.hint", { min: PASSWORD_MIN })}
          error={passwordError}
        />
        <FormError>{error}</FormError>
        <Button type="submit" disabled={pending}>
          {pending ? <Spinner /> : null}
          {t("reset.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
